var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/* eslint-disable @typescript-eslint/ban-ts-comment */
import _ from 'lodash';
import { css } from 'styled-components';
import { ACTION_PERMISSIONS, PATCH_MODAL_HEIGHT, PATCH_MODAL_WIDTH, PERMISSIONS, } from '../SbGlobal/constants';
import { sbColors as colors } from 'components/UI-helpers/colors';
var PATCH_MODAL_GUTTER = 16;
var PATCH_MODAL_X_SPACE = 160;
/**
 * The getPatchColorCodes function returns a color code based on the type of patch.
 *
 * @param type: string Determine the color of the patch
 * @return A color code for a given patch type
 */
export var getPatchColorCodes = function (type) {
    var _a;
    var typeColorCodes = {
        view: colors.purple,
        list: colors.purple,
        navigation: colors.marine,
        logic: colors.turquoise,
        logic_group: colors.blue,
        custom_logic: colors.blue,
    };
    return (_a = typeColorCodes[type]) !== null && _a !== void 0 ? _a : typeColorCodes.view;
};
/**
 * The appendOutToName function takes a string as an argument and returns the same string with 'out:' prepended to it.
 * If the input already has 'out:' at the beginning, it is returned unchanged.
 *
 * @param name Check if the name contains 'out:'
 * @return A string
 */
export var appendOutToName = function (name) {
    if (name === void 0) { name = ''; }
    return name.includes('out:') ? name : "out:".concat(name);
};
/**
 * The cleanPortName function takes a port name and removes the prefixes 'in:' or 'out:'.
 * It also removes double underscores.
 *
 * @param {string} name Get the name of the port
 * @return {string} The name of the node port
 */
export var cleanPortName = function (name) {
    var withoutPrefix = name.replace('in:', '').replace('out:', '');
    return withoutPrefix.replace(/__/g, '');
};
/**
 * The formatPatchType function takes a string value and replaces all underscores with spaces.
 *
 * @param value: string Get the value of the patch type
 * @return A string that replaces all underscores with spaces
 */
export var formatPatchType = function (value) { return value === null || value === void 0 ? void 0 : value.replace(/_/g, ' '); };
/**
 * The mergeCustomEvents function takes in an array of views and an array of selectedViewIds.
 * It returns a new array containing the outputs from each view that is included in the selectedViewIds.
 * If there are multiple views with outputs with the same name, only one output will be returned for that name.
 *
 * @param views Get the outputs from each view
 * @param selectedViewIds Filter the views that are passed in
 *
 * @return An array of objects
 */
export var mergeCustomEvents = function (views, selectedViewIds) {
    var datasetOutputs = [];
    views.forEach(function (view) {
        if (selectedViewIds.includes(view.id) && view.outputs.length) {
            var ids_1 = new Set(datasetOutputs.map(function (op) { return op.name; }));
            datasetOutputs = __spreadArray(__spreadArray([], datasetOutputs, true), view.outputs.filter(function (op) { return !ids_1.has(op.name); }), true);
        }
    });
    return datasetOutputs;
};
/**
 * The connectedEdges function takes in the count, id, fromLink and edges as parameters.
 * It then filters through the edges array to find all the connectedEdges that have a from_patch equal to id and a from_patch not equal to a to_patch.
 * If there are any connectedEdges found it will map over them and return an array of their respective 'to_patch' values.
 * It will then filter out any duplicates in this new array using indexOf().
 * The count is incremented by 1 each time this function is called recursively (which happens when there are still more connectedEdges)
 *
 * @param count Count how many times the function is called
 * @param id Find the edges that are connected to the patch
 * @param fromLink Check if the edge is connected to itself
 * @param edges Filter the edges that are connected to the current patch
 *
 * @return The connected edges of the selected patch
 */
var connectedEdges = function (count, id, fromLink, edges) {
    var connectedEdgesFilter = edges.filter(function (value) { return value.from_patch === id && value.from_patch !== value.to_patch; });
    if (connectedEdgesFilter.length > 0) {
        var connectedEdgesToPatch_1 = connectedEdgesFilter.map(function (item) { return item.to_patch; });
        var removeDuplicatesOfConnectedEdges = connectedEdgesToPatch_1.filter(function (edges, index) { return connectedEdgesToPatch_1.indexOf(edges) === index; });
        count++;
        return isRemoveLoops(count, removeDuplicatesOfConnectedEdges, fromLink, edges);
    }
    else {
        return true;
    }
};
/**
 * The isRemoveLoops function is used to remove loops from the graph.
 *
 * @param count Keep track of the number of edges that have been checked
 * @param filterEdges Filter the edges that are connected to the fromLink node
 * @param fromLink Check if the edge is connected to the fromLink
 * @param edges Check if the edge is connected to another edge
 *
 * @return A boolean value
 */
var isRemoveLoops = function (count, filterEdges, fromLink, edges) {
    var isConnected = true;
    filterEdges.forEach(function (id) {
        if (id === fromLink) {
            isConnected = false;
        }
        else {
            if (count < edges.length && isConnected) {
                isConnected = connectedEdges(count, id, fromLink, edges);
            }
            else {
                isConnected = false;
            }
        }
        return isConnected;
    });
    return isConnected;
};
/**
 * The findConnectedEdges function is used to determine if a connection between two nodes
 * can be made. It does this by checking the edges array in the patchwork object, and
 * filtering out any edges that have a from_patch value equal to the target node's id.
 * If there are no such edges, then it returns true (meaning that we can make a connection).
 * Otherwise, it checks whether there is already an edge connecting these two nodes. If so, then it returns false (meaning we cannot make another connection).
 * Otherwise, it calls itself recursively on each of the remaining connected nodes until either all connections
 *
 * @param patchwork Get the edges of the patchwork
 * @param source Get the source node, and the target parameter is used to get the target node
 * @param target Get the patchPort property of the target node
 *
 * @return A boolean value
 */
export var findConnectedEdges = function (patchwork, source, target) {
    var edges = patchwork.edges;
    var fromLink = source.getParent().getOptions().id;
    var toLink = target.getParent().getOptions().id;
    var count = 0;
    var filterEdges = edges.filter(function (val) { return val.from_patch === toLink && val.from_patch !== val.to_patch; });
    var getFromPatches = filterEdges.map(function (item) { return item.to_patch; });
    var removeDuplicatesOfFromPatches = getFromPatches.filter(function (edges, index) { return getFromPatches.indexOf(edges) === index; });
    var isFromToSame = toLink === fromLink;
    var sourcePort = source.getOptions().patchPort;
    if (sourcePort && sourcePort.name.includes('in:')) {
        return false;
    }
    var targetPort = target.patchPort;
    if (targetPort && targetPort.name.includes('out:')) {
        return false;
    }
    if (isFromToSame) {
        return false;
    }
    if (filterEdges.length > 0) {
        return isRemoveLoops(count, removeDuplicatesOfFromPatches, fromLink, edges);
    }
    else {
        return true;
    }
};
var sizes = {
    desktopL: 1270,
    desktop: 992,
    tablet: 768,
    phone: 576,
};
// Iterate through the sizes and create a media template
export var media = Object.keys(sizes).reduce(function (acc, label) {
    acc[label] = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    @media (max-width: ", "em) {\n      ", ";\n    }\n  "], ["\n    @media (max-width: ", "em) {\n      ", ";\n    }\n  "])), sizes[label] / 16, css.apply(void 0, args));
    };
    return acc;
}, {});
/**
 * The getModalPosition function takes in the clientX and clientY coordinates of a mouse event,
 * and returns an object with x and y properties that represent where the modal should be positioned.
 * The function first creates a modifiedModalPositionByMousePoint object that represents where the modal would be positioned if it were to appear directly below/to-the-right of the mouse cursor.
 * It then checks whether this position would cause any part of the modal to go offscreen, adjusting its x or y value accordingly if so.
 *
 * @param clientX Determine the x position of the modal
 * @param clientY Set the y position of the modal
 *
 * @return A position object with x and y coordinates
 */
export var getModalPosition = function (clientX, clientY) {
    var modifiedModalPositionByMousePoint = {
        x: clientX + PATCH_MODAL_X_SPACE,
        y: clientY,
    };
    return {
        x: clientX + PATCH_MODAL_WIDTH + PATCH_MODAL_GUTTER > window.innerWidth
            ? window.innerWidth - (PATCH_MODAL_WIDTH / 2 + PATCH_MODAL_GUTTER)
            : modifiedModalPositionByMousePoint.x,
        y: modifiedModalPositionByMousePoint.y +
            PATCH_MODAL_HEIGHT +
            PATCH_MODAL_GUTTER >
            window.innerHeight
            ? window.innerHeight - (PATCH_MODAL_HEIGHT + PATCH_MODAL_GUTTER) / 2
            : modifiedModalPositionByMousePoint.y < 280
                ? 280
                : modifiedModalPositionByMousePoint.y,
    };
};
/**
 * The isArraysEqual function takes two arrays as arguments and returns true if the arrays are equal,
 * or false if they are not. The function uses the lodash xorWith method to compare each element of
 * one array with each element of another array. If there is a difference between any elements in either
 * array, then the function will return false; otherwise it will return true. This means that both arrays must be exactly equal for this function to return true (i.e., order matters).
 *
 * @param baseArray: any[] Define the array that will be compared to the targetArray
 * @param targetArray: any[] Compare the baseArray: any[] parameter
 *
 * @return True if the two arrays are equal and false otherwise
 */
export var isArraysEqual = function (baseArray, targetArray) {
    return _(baseArray).xorWith(targetArray, _.isEqual).isEmpty();
};
/**
 * The isReadOnlyCanvas function takes an array of permissions and returns a boolean.
 * It checks if the user has read permission, but not any other action permissions.
 *
 * @param permissions: string[] Check if the user has read only permissions
 *
 * @return True if the user has only read permissions
 */
export var isReadOnlyCanvas = function (permissions) {
    return ((permissions === null || permissions === void 0 ? void 0 : permissions.includes(PERMISSIONS.READ)) &&
        !ACTION_PERMISSIONS.some(function (action) { return permissions === null || permissions === void 0 ? void 0 : permissions.includes(action); }));
};
/**
 * The buildSnapshotPreviewUrl function takes an appId, viewId and width as arguments
 * and returns a URL to the snapshot preview image for that view.
 *
 * @param appId: string Specify the appid of the application
 * @param viewId: string Build the snapshot preview url
 * @param width: number Determine the width of the image
 *
 * @return A string
 */
export var buildSnapshotPreviewUrl = function (appId, viewId, width) {
    return "".concat(process.env.MEDIA_BUCKET_URL, "/").concat(appId, "/").concat(viewId, "_view_snapshot.png?w=").concat(width);
};
/**
 * The selector function is a pure function that takes the entire Redux state
 * and returns a slice of it. This selector function should be used to select
 * values from the store for display in your React components. The selector
 * function will also be passed as an argument to mapStateToProps when you use
 * connect() to connect your component with the Redux store.
 * You can think of it as a way of selecting data from the store,
 * similar to how you would select data from an object or array using bracket notation or dot notation (e.g., myObject['key']).
 * In fact, this is exactly what happens under
 *
 * @param cb Pass in a callback function that will be called
 *
 * @return The value of the state object
 */
// export const selector = (cb) => {
//   return cb(store.getState());
// };
/**
 * The createStateSlicer function takes a slice of the Redux store and returns a function that
 * can be used to get values from that slice.
 *
 * @param slice: string Specify the slice of state that you want to access
 *
 * @return A function that takes a key and returns the value of that key in the state slice
 */
// export const createStateSlicer = (slice: string) => (key: string) => {
//   const stateSlice = store.getState()?.[slice] ?? undefined;
//   return stateSlice && key ? stateSlice?.[key] ?? undefined : undefined;
// };
/**
 * The buildDefaultName function takes in an array of strings and a prefix string.
 * It then returns a default name that is not already present in the array,
 * by appending an incrementing number to the end of the prefix.
 *
 * @param checkArray: any[] Check if the default name is already in use
 * @param prefix: string Build the default name
 *
 * @return A string that is not in the array
 */
export var buildDefaultName = function (checkArray, prefix) {
    var _a, _b;
    var siblings = (_a = checkArray === null || checkArray === void 0 ? void 0 : checkArray.filter(function (checkItem) { var _a; return (_a = checkItem === null || checkItem === void 0 ? void 0 : checkItem.toLowerCase()) === null || _a === void 0 ? void 0 : _a.includes(prefix === null || prefix === void 0 ? void 0 : prefix.toLowerCase()); })) !== null && _a !== void 0 ? _a : [];
    var counter = ((_b = siblings === null || siblings === void 0 ? void 0 : siblings.length) !== null && _b !== void 0 ? _b : 0) + 1;
    var defaultName = "".concat(prefix, " ").concat(counter);
    while (checkArray === null || checkArray === void 0 ? void 0 : checkArray.includes(defaultName)) {
        defaultName = "".concat(prefix, " ").concat(++counter);
    }
    return defaultName;
};
/**
 * The getNodeElementByNodeId function returns the DOM element of a node with a given id.
 *
 * @param nodeId: string Identify the node that we want to get
 * @param filter?: string Filter the elements by classname
 *
 * @return An element
 */
export var getNodeElementByNodeId = function (nodeId, filter) {
    var _a, _b;
    var elements = document.querySelectorAll("[data-nodeid=\"".concat(nodeId, "\"]"));
    return filter
        ? (_a = Array.from(elements)) === null || _a === void 0 ? void 0 : _a.find(function (element) { var _a; return (_a = element === null || element === void 0 ? void 0 : element.className) === null || _a === void 0 ? void 0 : _a.includes(filter); })
        : (_b = Array.from(elements)) === null || _b === void 0 ? void 0 : _b[0];
};
var templateObject_1;
